import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  FileText,
  Home,
  LockKeyhole,
  UserRound,
  Users,
  Sparkles,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import type { Row } from "../api";
import { FederalCalculator } from "./FederalCalculator";
import { Button, Empty } from "../ui";
import {
  buildTaxTopics,
  topicDisplayValue,
  topicConflicts,
  type TaxTopicBinding,
} from "../tax-topics";
import { buildInterviewPages, type InterviewPage } from "../interview-pages";
import {
  pageFieldRoot,
  pageIssues,
  pageNeedsReview,
  mergePageValue,
} from "../interview-page-values";
import {
  builderSchema,
  builderValue,
  builderSaveValue,
  builderPageForProfile,
  builderItemTitle,
  builderItemLabel,
} from "../return-builder-fields";
import {
  buildReturnBlueprint,
  returnBuilderStages,
  type ReturnBuilderStage,
} from "../return-builder-model";
import {
  readReturnBuilderDraft,
  formatTaxId,
  type ReturnBuilderDraft,
} from "../return-builder-draft";
import {
  deriveFilingInterview,
  parseFilingInterview,
  type FilingInterview,
} from "../filing-status";
import { filingStatusLabels } from "../filing-status-presentation";
import {
  buildTopicRequest,
  saveTopicRequest,
  cloneTopicValue,
  mergeTopicEdit,
  topicContainer,
  topicKey,
  topicValueKey,
  type TopicChange,
} from "../topic-save";
import {
  normalizeTaxAge,
  taxAnswerView,
  isTaxObject,
} from "../tax-field-values";
import {
  fieldTargetId,
  validateTaxFields,
  type FieldIssue,
} from "../field-validation";
import { TaxFields } from "./TaxFields";
import { FilingStatusInterview } from "./FilingStatusInterview";
import { ReturnProfileCards } from "./ReturnProfileCards";
import { PreparationCoverage } from "./PreparationCoverage";
import { ReturnDiagnostics } from "./ReturnDiagnostics";
import { ValidationSummary, focusValidationIssue } from "./ValidationSummary";
import "./topic-interview.css";
import "./return-builder.css";

interface Props {
  catalog: Row;
  selected: Row | null;
  role: string;
  refresh: () => Promise<void>;
  notify: (message: string) => void;
  initialStage?: string;
  navigate: (path: string) => void;
  onTechnicalDetails?: (record: Row) => void;
  renderDocuments?: () => ReactNode;
  diagnosticsEnabled?: boolean;
}
export function initialBuilderStage(path: string): string {
  if (/reconciliation|admission|review/.test(path)) return "review";
  if (path === "/forms/1040") return "federal-return";
  if (/w2-upload|evidence-upload/.test(path)) return "documents";
  return "about-taxpayer";
}
const instructions: Record<string, string> = {
  "about-taxpayer":
    "The essentials, together in one place. Add taxpayer details and choose a filing status.",
  "return-profile":
    "Choose the topics that belong in this return. We’ll organize the workspace around them.",
  documents:
    "Keep the taxpayer’s source documents together, ready for preparation and review.",
  income: "Work through the income sources selected for this return.",
  "deductions-credits":
    "Explore the deductions and credits relevant to the selected return topics.",
  review:
    "Review the information collected and the work still needed before a return can be prepared.",
  "federal-return":
    "The destination for the prepared federal return and supporting workpapers.",
};
const personalPages = [
  "filing-status-taxpayer-name",
  "filing-status-taxpayer-birth",
  "filing-status-taxpayer-occupation",
  "filing-status-taxpayer-contact",
];
const extraPages = [
  "filing-status-tax-year",
  "filing-status-taxpayer-residence",
];
const cleanMessage = (message: string) =>
  message
    .replace(
      /Q-\d+|SC-[A-Z-]+|INT\.[\w.]+|\b(?:schema|contract|PRECALC)\b/gi,
      "saved information",
    )
    .replace(
      /Select Not sure if you need to follow up\./g,
      "Use the taxpayer’s records to complete this item.",
    );

/** Presentation-only builder. Source bindings, role checks, CAS saves, and tax engine remain unchanged. */
export function ReturnBuilder({
  catalog,
  selected,
  role,
  refresh,
  notify,
  initialStage = "about-taxpayer",
  onTechnicalDetails,
  renderDocuments,
  diagnosticsEnabled = false,
}: Props) {
  const topics = useMemo(() => buildTaxTopics(catalog), [catalog]);
  const sourceCards = useMemo(
    () => [
      ...new Map(
        topics
          .flatMap((t) => t.blocks.flatMap((b) => b.bindings))
          .map((c) => [topicKey(c), c]),
      ).values(),
    ],
    [topics],
  );
  const [active, setActive] = useState(() => {
    const saved =
      typeof window === "undefined"
        ? null
        : new URLSearchParams(window.location.hash.slice(1)).get("builder");
    return returnBuilderStages.some((s) => s.id === saved)
      ? saved!
      : initialStage;
  });
  const [drafts, setDrafts] = useState<Record<string, unknown>>({});
  const [baseline, setBaseline] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false),
    [blocked, setBlocked] = useState(false);
  const [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [pendingStage, setPendingStage] = useState<string | null>(null);
  const [attempted, setAttempted] = useState<Record<string, boolean>>({});
  const [extraOpen, setExtraOpen] = useState(false),
    [showSsn, setShowSsn] = useState(false);
  const [openModule, setOpenModule] = useState<string | null>(null);
  const [focusIssue, setFocusIssue] = useState<FieldIssue | null>(null);
  const pending = useRef(false),
    heading = useRef<HTMLHeadingElement>(null);
  const writable = ["workspace_admin", "preparer"].includes(role);
  const dirty = Object.keys(drafts).length > 0;
  function valueFor(card: TaxTopicBinding): unknown {
    return Object.hasOwn(drafts, topicKey(card))
      ? drafts[topicKey(card)]
      : selected
        ? normalizeTaxAge(topicDisplayValue(card, selected))
        : undefined;
  }
  const effective: Row = {
    ...selected,
    facts: { ...selected?.facts },
    answers: { ...selected?.answers },
  };
  for (const card of sourceCards)
    if (Object.hasOwn(drafts, topicKey(card))) {
      const container = topicContainer(card);
      effective[container][card.record.id] = mergeTopicEdit(
        effective[container][card.record.id],
        selected ? topicDisplayValue(card, selected) : undefined,
        drafts[topicKey(card)],
      );
    }
  const filingCard = sourceCards.find((c) => c.record.id === "Q-008")!;
  const filingRaw = filingCard ? valueFor(filingCard) : undefined;
  const filing = parseFilingInterview(filingRaw);
  let builder: ReturnBuilderDraft = {
    version: 1,
    profile: { version: 1, selected: [], confirmed: false },
  };
  let draftProblem = "";
  try {
    builder = readReturnBuilderDraft(filingRaw);
  } catch (reason) {
    draftProblem = (reason as Error).message;
  }
  const pages = buildInterviewPages(topics, effective, valueFor).flatMap(
    (page) => {
      const projected = builderPageForProfile(page, builder.profile);
      return projected ? [projected] : [];
    },
  );
  const blueprint = buildReturnBlueprint(pages, builder.profile, filingRaw);
  const stage =
    blueprint.stages.find((s) => s.id === active) || blueprint.stages[0];
  const index = blueprint.stages.indexOf(stage);
  const current = useRef({ selected, stageId: stage.id });
  current.current = { selected, stageId: stage.id };
  const disabled = !writable || busy || blocked || Boolean(draftProblem);
  const birthFields = extraOpen ? undefined : ["date_of_birth", "age_for_2025"];
  function visiblePages(forStage: ReturnBuilderStage): InterviewPage[] {
    if (forStage.id === "about-taxpayer")
      return forStage.pages.filter(
        (p) => extraOpen || !extraPages.includes(p.id),
      );
    if (["review", "federal-return"].includes(forStage.id)) return [];
    if (forStage.id === "documents")
      return forStage.pages.filter(
        (p) => p.id === "income-wage-statement-count",
      );
    if (["income", "deductions-credits"].includes(forStage.id))
      return forStage.groups.find((g) => g.id === openModule)?.pages || [];
    return forStage.pages.filter((p) => !p.reviewOnly && !p.documents);
  }
  function issuesFor(page: InterviewPage, card: TaxTopicBinding): FieldIssue[] {
    if (!card.available || page.reviewOnly || page.documents) return [];
    if (card.record.id === "Q-008")
      return pageIssues(
        page,
        card,
        valueFor(card),
        effective,
        selected?.answers?.["Q-008"],
      );
    const fields =
      page.id === "filing-status-taxpayer-birth" ? birthFields : undefined;
    return validateTaxFields(
      builderSchema(page, card, fields),
      builderValue(page, valueFor(card), fields),
      {
        rootId: pageFieldRoot(page, card),
        sectionId: page.id,
        sectionLabel: page.title,
        rootLabel: page.title,
      },
    ).map((issue) => {
      const customRows =
        page.itemIds &&
        !["CONFLICT", "NOT_APPLICABLE"].includes(
          (valueFor(card) as Row)?.state,
        );
      const itemId =
        page.itemIds?.find((id) => issue.path.includes(id)) ||
        (customRows && issue.path.length === 0 ? page.itemIds?.[0] : undefined);
      return itemId
        ? {
            ...issue,
            path: ["items", itemId],
            targetId: fieldTargetId(pageFieldRoot(page, card), [
              "items",
              itemId,
            ]),
            fieldLabel: builderItemTitle(card, itemId),
            reason:
              issue.kind === "required"
                ? "Choose Applies or Does not apply using the taxpayer’s records."
                : cleanMessage(issue.reason),
          }
        : issue;
    });
  }
  function ssnIssues(): FieldIssue[] {
    const value = builder.taxpayer?.ssn || "";
    if (/^\d{3}-\d{2}-\d{4}$/.test(value)) return [];
    return [
      {
        id: "rb-taxpayer-ssn-error",
        targetId: "rb-taxpayer-ssn",
        path: ["ssn"],
        sectionId: "filing-status-taxpayer-name",
        sectionLabel: "Taxpayer information",
        fieldLabel: "Social Security number",
        kind: value ? "invalid" : "required",
        reason: value
          ? "Enter exactly nine digits. The number is formatted as you type; this does not verify identity."
          : "Enter the taxpayer’s nine-digit Social Security number.",
      },
    ];
  }
  const currentIssues = [
    ...visiblePages(stage).flatMap((p) =>
      p.bindings.flatMap((c) => issuesFor(p, c)),
    ),
    ...(stage.id === "about-taxpayer" ? ssnIssues() : []),
  ].map((i) => ({ ...i, reason: cleanMessage(i.reason) }));
  const shownIssues = currentIssues.filter(
    (i) => attempted[stage.id] || i.kind === "invalid",
  );
  const allReviewIssues = blueprint.stages
    .filter((s) => !["review", "federal-return"].includes(s.id))
    .flatMap((s) =>
      s.pages
        .filter(
          (p) =>
            !extraPages.includes(p.id) &&
            (s.id !== "documents" || p.id === "income-wage-statement-count"),
        )
        .flatMap((p) => p.bindings.flatMap((c) => issuesFor(p, c))),
    )
    .concat(ssnIssues());
  const overlaps = [
    ...new Map(
      topics.flatMap((t) => topicConflicts(t, effective)).map((f) => [f.id, f]),
    ).values(),
  ];
  const filingAnalysis = deriveFilingInterview(
    filing,
    effective,
    selected?.answers?.["Q-008"],
  );
  function stageState(
    s: ReturnBuilderStage,
  ): "Completed" | "Missing" | "Needs review" {
    if (s.id === "return-profile")
      return builder.profile.confirmed &&
        topicValueKey(builder.profile) ===
          topicValueKey(
            parseFilingInterview(selected?.answers?.["Q-008"]).returnBuilder
              ?.profile,
          )
        ? "Completed"
        : "Missing";
    if (["documents", "review", "federal-return"].includes(s.id))
      return "Needs review";
    const missing = s.pages
      .filter((p) => !extraPages.includes(p.id))
      .flatMap((p) => p.bindings.flatMap((c) => issuesFor(p, c)));
    if (s.id === "about-taxpayer") missing.push(...ssnIssues());
    if (missing.some((i) => i.kind === "required")) return "Missing";
    if (
      missing.length ||
      s.groups.some((g) => g.preparationSupport === "review-only") ||
      s.pages.some((p) =>
        p.bindings.some((c) =>
          pageNeedsReview(
            p,
            c,
            valueFor(c),
            effective,
            selected?.answers?.["Q-008"],
          ),
        ),
      ) ||
      s.pages.some((p) =>
        p.bindings.some((c) => Object.hasOwn(drafts, topicKey(c))),
      ) ||
      overlaps.length ||
      (s.id === "about-taxpayer" && filingAnalysis.conflicts.length)
    )
      return "Needs review";
    return "Completed";
  }
  useEffect(() => {
    const hash = "#builder=" + stage.id;
    if (window.location.hash !== hash)
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + window.location.search + hash,
      );
    heading.current?.focus({ preventScroll: true });
    heading.current?.scrollIntoView({ block: "start", behavior: "instant" });
    setShowSsn(false);
  }, [stage.id]);
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("zt:intake-dirty", { detail: { dirty: dirty || busy } }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("zt:intake-dirty", { detail: { dirty: false } }),
      );
    };
  }, [dirty, busy]);
  useEffect(() => {
    if (focusIssue && focusValidationIssue(focusIssue)) setFocusIssue(null);
  }, [focusIssue, stage.id, openModule, extraOpen]);
  function edit(
    card: TaxTopicBinding,
    value: unknown,
    rendered = valueFor(card),
  ) {
    if (!selected || pending.current || !writable || blocked || draftProblem)
      return;
    if (!baseline) setBaseline(cloneTopicValue(selected) as Row);
    const original = topicDisplayValue(card, baseline || selected),
      key = topicKey(card);
    setDrafts((previous) => {
      const next = { ...previous },
        latest = Object.hasOwn(previous, key) ? previous[key] : original;
      const merged = normalizeTaxAge(mergeTopicEdit(latest, rendered, value));
      if (topicValueKey(original) === topicValueKey(merged)) delete next[key];
      else next[key] = merged;
      return next;
    });
    setError("");
    setNotice("");
  }
  function updateFiling(next: FilingInterview) {
    try {
      edit(
        filingCard,
        deriveFilingInterview(next, effective, selected?.answers?.["Q-008"])
          .answer,
      );
    } catch (reason) {
      setError(cleanMessage((reason as Error).message));
    }
  }
  function updateBuilder(next: ReturnBuilderDraft) {
    updateFiling({ ...filing, version: 2, returnBuilder: next });
  }
  function go(id: string) {
    setActive(id);
    setPendingStage(null);
    setError("");
    setNotice("");
    setOpenModule(null);
  }
  function move(id: string) {
    if (busy || id === stage.id) return;
    if (dirty) setPendingStage(id);
    else go(id);
  }
  function selectIssue(issue: FieldIssue) {
    if (pending.current || busy) return;
    const owner = blueprint.stages.find((s) =>
      s.pages.some((p) => p.id === issue.sectionId),
    );
    if (!owner) return;
    // In-builder navigation retains the entire local draft; no source edits are discarded.
    setActive(owner.id);
    setAttempted((old) => ({ ...old, [owner.id]: true }));
    setOpenModule(
      owner.groups.find((g) => g.pages.some((p) => p.id === issue.sectionId))
        ?.id || null,
    );
    if (extraPages.includes(issue.sectionId)) setExtraOpen(true);
    setFocusIssue(issue);
  }
  async function save(continueAfter: boolean, confirmProfile = false) {
    if (!selected || disabled || pending.current) return;
    if (continueAfter) setAttempted((old) => ({ ...old, [stage.id]: true }));
    if (continueAfter && !pendingStage && currentIssues.length) {
      selectIssue(currentIssues[0]);
      return;
    }
    const requestedNext = pendingStage || blueprint.stages[index + 1]?.id;
    const work = { ...drafts };
    if (confirmProfile)
      work[topicKey(filingCard)] = deriveFilingInterview(
        {
          ...filing,
          version: 2,
          returnBuilder: {
            ...builder,
            profile: { ...builder.profile, confirmed: true },
          },
        },
        effective,
        selected.answers?.["Q-008"],
      ).answer;
    if (!Object.keys(work).length) {
      if (continueAfter && requestedNext) go(requestedNext);
      return;
    }
    const snapshot = baseline || selected,
      attemptStage = stage.id,
      attemptReturn = selected.id;
    if (
      snapshot.id !== selected.id ||
      snapshot.revision !== selected.revision
    ) {
      setError(
        "This return changed while you were working. Your entries are preserved. Reload the latest information before saving.",
      );
      setBlocked(true);
      return;
    }
    const changes: TopicChange[] = sourceCards
      .filter((c) => Object.hasOwn(work, topicKey(c)))
      .map((card) => ({
        card,
        value: builderSaveValue(card, work[topicKey(card)]),
      }));
    let request;
    try {
      request = buildTopicRequest(
        snapshot,
        role,
        "personal-information",
        changes,
      );
    } catch (reason) {
      setError(cleanMessage((reason as Error).message));
      if (currentIssues.length) selectIssue(currentIssues[0]);
      return;
    }
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const confirmed = await saveTopicRequest(request);
      if (
        current.current.selected?.id !== attemptReturn ||
        current.current.stageId !== attemptStage
      ) {
        notify("Information was saved to the previously selected return.");
        return;
      }
      setDrafts({});
      setBaseline(null);
      setNotice("Changes saved");
      setPendingStage(null);
      try {
        await refresh();
      } catch {
        setError(
          "Your changes were saved, but the latest return could not be loaded. Reload before making more changes.",
        );
        setBlocked(true);
        return;
      }
      if (
        continueAfter &&
        current.current.selected?.id === attemptReturn &&
        current.current.stageId === attemptStage
      ) {
        const nextPages = buildInterviewPages(topics, confirmed, (c) =>
          normalizeTaxAge(topicDisplayValue(c, confirmed)),
        );
        const nextBlueprint = buildReturnBlueprint(
          nextPages,
          readReturnBuilderDraft(confirmed.answers?.["Q-008"]).profile,
          confirmed.answers?.["Q-008"],
        );
        const next =
          nextBlueprint.stages.find((s) => s.id === requestedNext) ||
          nextBlueprint.stages[
            nextBlueprint.stages.findIndex((s) => s.id === attemptStage) + 1
          ];
        if (next) go(next.id);
      }
    } catch (reason) {
      if (current.current.selected?.id === attemptReturn) {
        setError(
          (reason as Row)?.code === "REVISION_CONFLICT"
            ? "Someone updated this return while you were working. Your entries have not overwritten their changes. Reload before saving again."
            : "The save could not be confirmed. Your entries are preserved. Reload before retrying because the changes may already have been saved.",
        );
        setBlocked(true);
      } else
        notify(
          "A save to the previous return could not be confirmed. Reopen that return before retrying.",
        );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function reload() {
    if (
      pending.current ||
      (dirty &&
        !window.confirm(
          "Reload the latest return and discard unsaved changes?",
        ))
    )
      return;
    pending.current = true;
    setBusy(true);
    try {
      await refresh();
      setDrafts({});
      setBaseline(null);
      setBlocked(false);
      setError("");
      setPendingStage(null);
    } catch {
      setError(
        "The latest return could not be loaded. Your entries are still here.",
      );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  function renderPage(page: InterviewPage, fieldNames?: string[]) {
    if (page.reviewOnly || page.documents) return null;
    return page.bindings.map((card) => {
      const whole = valueFor(card),
        shown = builderValue(page, whole, fieldNames),
        root = pageFieldRoot(page, card);
      if (card.record.id === "Q-008")
        return (
          <FilingStatusInterview
            key={page.id + topicKey(card)}
            value={whole}
            selected={effective}
            previousRaw={selected?.answers?.["Q-008"]}
            rootId={root}
            disabled={disabled}
            builderMode
            showRequiredErrors={Boolean(attempted[stage.id])}
            visibleFields={page.filingFields?.filter(
              (f) => f !== "filingStatus",
            )}
            pageTitle={page.title}
            showHouseholdSummary={false}
            onChange={updateFiling}
            onDependents={() =>
              document
                .getElementById("rb-dependents")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          />
        );
      if (!card.available)
        return (
          <p className="rb-field-hint" key={page.id + topicKey(card)}>
            Keep the supporting records for review. This entry requires a
            preparation service that is not connected yet.
          </p>
        );
      if (
        page.itemIds &&
        !["CONFLICT", "NOT_APPLICABLE"].includes((whole as Row)?.state)
      ) {
        const data = taxAnswerView(shown).value;
        const items =
          isTaxObject(data) && isTaxObject(data.items) ? data.items : {};
        const issues = issuesFor(page, card);
        return (
          <div className="rb-item-list" key={page.id + topicKey(card)}>
            {page.itemIds.map((itemId) => {
              const issue = issues.find(
                (i) =>
                  i.path.includes(itemId) &&
                  (attempted[stage.id] || i.kind === "invalid"),
              );
              const targetId =
                issue?.targetId || fieldTargetId(root, ["items", itemId]);
              const title = builderItemTitle(card, itemId);
              return (
                <div
                  className="rb-item-row"
                  key={itemId}
                  id={targetId}
                  role="group"
                  aria-label={title}
                  aria-invalid={Boolean(issue)}
                  aria-describedby={issue ? targetId + "-error" : undefined}
                >
                  <div>
                    <strong>{title}</strong>
                    <small>{builderItemLabel(card, itemId)}</small>
                    {issue && (
                      <p
                        id={targetId + "-error"}
                        className="field-validation-error"
                      >
                        {title}: Choose whether this applies using the
                        taxpayer’s records.
                      </p>
                    )}
                  </div>
                  <div className="rb-item-actions">
                    {(
                      [
                        ["PRESENT", "Applies"],
                        ["ABSENT", "Does not apply"],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        disabled={disabled}
                        aria-pressed={items[itemId] === value}
                        onClick={() => {
                          const next = {
                            state: "KNOWN",
                            value: {
                              ...(isTaxObject(data) ? data : {}),
                              items: { ...items, [itemId]: value },
                            },
                          };
                          try {
                            edit(
                              card,
                              mergePageValue(page, whole, shown, next),
                              whole,
                            );
                          } catch (reason) {
                            setError(cleanMessage((reason as Error).message));
                          }
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
      return (
        <div className="rb-field-cluster" key={page.id + topicKey(card)}>
          <TaxFields
            schema={builderSchema(page, card, fieldNames)}
            value={shown}
            label={page.title}
            rootId={root}
            sectionId={page.id}
            sectionLabel={page.title}
            disabled={disabled}
            builderMode
            showRequiredErrors={Boolean(attempted[stage.id])}
            onChange={(next) => {
              try {
                edit(card, mergePageValue(page, whole, shown, next), whole);
              } catch (reason) {
                setError(cleanMessage((reason as Error).message));
              }
            }}
          />
        </div>
      );
    });
  }
  const pageById = (id: string) => stage.pages.find((p) => p.id === id);
  const renderById = (id: string, fields?: string[]) => {
    const page = pageById(id);
    return page ? renderPage(page, fields) : null;
  };
  if (!selected)
    return (
      <Empty
        title="Choose a return to start building"
        description="Select a return above, or create a new return for your taxpayer."
      />
    );
  const ssnIssue = ssnIssues().find(
    (i) => attempted[stage.id] || i.kind === "invalid",
  );
  const statusPage = pages.find((p) => p.id === "filing-status-choice");
  const statusRoot =
    statusPage && filingCard
      ? fieldTargetId(pageFieldRoot(statusPage, filingCard), ["filingStatus"])
      : "rb-filing-status";
  const statusIssue = shownIssues.find(
    (issue) => issue.targetId === statusRoot,
  );
  return (
    <div className="return-builder">
      <aside className="rb-rail">
        <div className="rb-brand-mark">
          <Sparkles size={19} />
          <div>
            Build this return<small>Federal · 2025</small>
          </div>
        </div>
        <nav aria-label="Return Builder">
          <ol className="rb-stage-nav">
            {blueprint.stages.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  disabled={busy}
                  aria-current={s.id === stage.id ? "step" : undefined}
                  onClick={() => move(s.id)}
                >
                  <span className="rb-stage-icon" aria-hidden="true">
                    {stageState(s) === "Completed" ? (
                      <Check size={16} />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span>
                    {s.label}
                    <small>{stageState(s)}</small>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
        <div className="rb-progress">
          <span>Preparation progress</span>
          <progress
            max={blueprint.stages.length}
            value={
              blueprint.stages.filter((s) => stageState(s) === "Completed")
                .length
            }
            aria-label="Sections with information recorded"
          />
          <small>Section progress is not return approval.</small>
        </div>
        <div className="rb-summary">
          <h3>Return topics</h3>
          {builder.profile.confirmed ? (
            <ul className="rb-blueprint-list">
              {blueprint.selectedCards.map((c) => (
                <li key={c.id}>
                  <Check size={14} />
                  {c.label}
                </li>
              ))}
            </ul>
          ) : (
            <p>Choose a return profile to build your workspace.</p>
          )}
          <button
            type="button"
            onClick={() => move("return-profile")}
            disabled={busy}
          >
            Edit return profile <ArrowRight size={14} />
          </button>
        </div>
      </aside>
      <section className="rb-main" aria-label={stage.label}>
        <header className="rb-page-header">
          <span className="rb-eyebrow">YOUR RETURN WORKSPACE</span>
          <h2 tabIndex={-1} ref={heading}>
            {stage.label}
          </h2>
          <p>{instructions[stage.id]}</p>
        </header>
        {!writable && (
          <div className="rb-guard">
            Read-only view. A preparer can update this return.
          </div>
        )}
        {(error || draftProblem) && (
          <div className="rb-attention" role="alert">
            <strong>Return requires attention</strong>
            <p>{error || draftProblem}</p>
            {blocked && (
              <Button onClick={() => void reload()} disabled={busy}>
                <RefreshCw size={16} />
                Reload latest return
              </Button>
            )}
          </div>
        )}
        <ValidationSummary
          title="Return requires attention"
          issues={shownIssues}
          onSelect={selectIssue}
        />
        {stage.id === "about-taxpayer" && (
          <>
            <section className="rb-card">
              <div className="rb-card-heading">
                <span>
                  <UserRound size={21} />
                </span>
                <div>
                  <h3>Taxpayer Information</h3>
                  <p>Synthetic workspace · Use test information only.</p>
                </div>
              </div>
              <div className="rb-personal-grid">
                {renderById(personalPages[0])}
                <div className="rb-field rb-ssn">
                  <label htmlFor="rb-taxpayer-ssn">
                    Social Security number
                  </label>
                  <div>
                    <input
                      id="rb-taxpayer-ssn"
                      type={showSsn ? "text" : "password"}
                      autoComplete="off"
                      inputMode="numeric"
                      value={builder.taxpayer?.ssn || ""}
                      disabled={disabled}
                      aria-invalid={Boolean(ssnIssue)}
                      aria-describedby={
                        ssnIssue ? "rb-ssn-error rb-ssn-help" : "rb-ssn-help"
                      }
                      onChange={(e) =>
                        updateBuilder({
                          ...builder,
                          taxpayer: {
                            ...builder.taxpayer,
                            ssn: formatTaxId(e.target.value),
                          },
                        })
                      }
                    />
                    <button
                      type="button"
                      aria-label={
                        showSsn
                          ? "Hide Social Security number"
                          : "Show Social Security number"
                      }
                      aria-pressed={showSsn}
                      onClick={() => setShowSsn(!showSsn)}
                    >
                      {showSsn ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  <small id="rb-ssn-help">
                    Draft entry · Identity verification is separate.
                  </small>
                  {ssnIssue && (
                    <p id="rb-ssn-error" className="field-validation-error">
                      {ssnIssue.reason}
                    </p>
                  )}
                </div>
                {renderById(personalPages[1], birthFields)}
                {renderById(personalPages[2])}
                {renderById(personalPages[3])}
              </div>
              <details
                className="rb-extra-details"
                open={extraOpen}
                onToggle={(e) => setExtraOpen(e.currentTarget.open)}
              >
                <summary>Additional taxpayer details</summary>
                <div className="rb-form-grid">
                  {extraOpen &&
                    extraPages.map((id) => (
                      <div key={id}>{renderById(id)}</div>
                    ))}
                </div>
              </details>
            </section>
            <section className="rb-card rb-address">
              <div className="rb-card-heading">
                <span>
                  <Home size={21} />
                </span>
                <div>
                  <h3>Address</h3>
                  <p>Search and verify an address, or enter it manually.</p>
                </div>
              </div>
              {renderById("filing-status-taxpayer-address")}
              <div className="rb-field">
                <label htmlFor="rb-county">
                  County <span>(optional)</span>
                </label>
                <input
                  id="rb-county"
                  value={builder.taxpayer?.county || ""}
                  maxLength={100}
                  disabled={disabled}
                  onChange={(e) =>
                    updateBuilder({
                      ...builder,
                      taxpayer: { ...builder.taxpayer, county: e.target.value },
                    })
                  }
                />
                <small>
                  Entered by the preparer; not part of postal verification.
                </small>
              </div>
            </section>
            <section className="rb-card">
              <div className="rb-card-heading">
                <span>
                  <FileText size={21} />
                </span>
                <div>
                  <h3>Filing Status</h3>
                  <p>
                    Choose the intended filing status. Relevant household
                    details appear below.
                  </p>
                </div>
              </div>
              <div
                id={statusRoot}
                className="rb-filing-options"
                role="group"
                aria-label="Filing status"
                aria-invalid={Boolean(statusIssue)}
                aria-describedby={statusIssue ? "rb-status-error" : undefined}
              >
                {Object.entries(filingStatusLabels).map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={filing.filingStatus === value}
                    disabled={disabled}
                    onClick={() =>
                      updateFiling({
                        ...filing,
                        version: 2,
                        filingStatus: value as FilingInterview["filingStatus"],
                      })
                    }
                  >
                    <span>{label}</span>
                    {filing.filingStatus === value && <Check size={17} />}
                  </button>
                ))}
              </div>
              {statusIssue && (
                <p className="field-validation-error" id="rb-status-error">
                  Filing status: {statusIssue.reason}
                </p>
              )}
              <div className="rb-filing-details">
                {renderById("filing-status-relationship")}
              </div>
            </section>
            {stage.groups
              .filter((g) =>
                ["spouse-information", "dependents"].includes(g.id),
              )
              .map((g) => (
                <section className="rb-card" id={"rb-" + g.id} key={g.id}>
                  <div className="rb-card-heading">
                    <span>
                      <Users size={21} />
                    </span>
                    <div>
                      <h3>{g.title}</h3>
                      <p>{g.description}</p>
                    </div>
                  </div>
                  <div className="rb-form-grid">
                    {g.pages.map((p) => (
                      <div key={p.id}>{renderPage(p)}</div>
                    ))}
                  </div>
                  {g.id === "dependents" && (
                    <p className="rb-field-hint">
                      Detailed dependent preparation is not connected yet. Keep
                      names, identification, and supporting records with the
                      return for review.
                    </p>
                  )}
                </section>
              ))}
          </>
        )}
        {stage.id === "return-profile" && (
          <>
            <ReturnProfileCards
              value={builder.profile}
              showHeading={false}
              disabled={disabled}
              onChange={(profile) =>
                updateBuilder({
                  ...builder,
                  profile: profile as ReturnBuilderDraft["profile"],
                })
              }
            />
            <div className="rb-summary">
              <h3>Your return, organized</h3>
              <p>
                Selected topics create the workspace. Removing a topic hides its
                section but preserves its saved information.
              </p>
              <p>
                Business, rental, and investment records can be organized for
                review; preparing those tax schedules is not available in this
                release.
              </p>
            </div>
          </>
        )}
        {stage.id === "documents" && (
          <>
            <section className="rb-card">
              <div className="rb-card-heading">
                <span>
                  <FileText size={21} />
                </span>
                <div>
                  <h3>Tax documents</h3>
                  <p>
                    Upload source records and keep track of documents still
                    needed.
                  </p>
                </div>
              </div>
              {stage.pages
                .filter((p) => p.id === "income-wage-statement-count")
                .map((p) => (
                  <div key={p.id}>{renderPage(p)}</div>
                ))}
              {renderDocuments?.()}
            </section>
          </>
        )}
        {["income", "deductions-credits"].includes(stage.id) && (
          <div className="rb-module-grid">
            {stage.groups.map((g) => (
              <section className="rb-module-card" key={g.id}>
                <button
                  className="rb-module-heading"
                  type="button"
                  aria-expanded={openModule === g.id}
                  aria-controls={"module-" + g.id}
                  onClick={() =>
                    setOpenModule(openModule === g.id ? null : g.id)
                  }
                >
                  <span>
                    <h3>{g.title}</h3>
                    <small>{g.description}</small>
                  </span>
                  <ChevronDown size={20} />
                </button>
                {openModule === g.id && (
                  <div id={"module-" + g.id} className="rb-module-body">
                    {g.pages.map((p) => (
                      <div key={p.id} className="rb-module-topic">
                        <h4>{p.title}</h4>
                        {renderPage(p)}
                      </div>
                    ))}
                    {g.preparationSupport === "review-only" && (
                      <p className="rb-field-hint">
                        Records captured here remain subject to review. This
                        workspace does not calculate or prepare these tax
                        schedules.
                      </p>
                    )}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
        {stage.id === "review" && (
          <>
            {diagnosticsEnabled && <ReturnDiagnostics returnId={selected.id} revision={selected.revision} hasUnsavedChanges={dirty || busy} navigateTo={destination => move(destination)} />}
            <PreparationCoverage
              caseId={selected.id}
              revision={selected.revision}
              hasUnsavedChanges={dirty || busy}
            />
            <section className="rb-card">
              <div className="rb-card-heading">
                <span>
                  <Check size={21} />
                </span>
                <div>
                  <h3>What remains?</h3>
                  <p>
                    Review the selected topics and supporting records before
                    preparation.
                  </p>
                </div>
              </div>
              <div className="rb-review-grid">
                {blueprint.stages
                  .filter((s) => !["review", "federal-return"].includes(s.id))
                  .map((s) => (
                    <button
                      type="button"
                      className="rb-review-item"
                      key={s.id}
                      onClick={() => move(s.id)}
                    >
                      <span>{s.label}</span>
                      <strong>{stageState(s)}</strong>
                      <ArrowRight size={16} />
                    </button>
                  ))}
              </div>
            </section>
            <ValidationSummary
              title="Return requires attention"
              issues={allReviewIssues.map((i) => ({
                ...i,
                reason: cleanMessage(i.reason),
              }))}
              onSelect={selectIssue}
            />
            {(filingAnalysis.conflicts.length > 0 || overlaps.length > 0) && (
              <section className="rb-attention">
                <h3>Information to reconcile</h3>
                {filingAnalysis.conflicts.map((f, i) => (
                  <p key={i}>{cleanMessage(f.message)}</p>
                ))}
                {overlaps.map((f) => (
                  <p key={f.id}>{cleanMessage(f.message)}</p>
                ))}
                <Button onClick={() => move("about-taxpayer")}>
                  Review taxpayer details
                </Button>
              </section>
            )}
            <section className="rb-card">
              <h3>Preparation still needed</h3>
              <p>
                Document verification, full tax review, and federal return
                preparation are not connected in this workspace. Topics not
                selected in the return profile have not been treated as “No.”
              </p>
              <p>
                No return has been calculated, approved, or certified by this
                builder.
              </p>
            </section>
          </>
        )}
        {stage.id === "federal-return" && selected && <FederalCalculator key={`${selected.id}:${selected.revision}`} selected={selected} role={role}/>}
        {pendingStage && (
          <div className="rb-guard" role="alert">
            <h3>Keep your changes?</h3>
            <p>
              Save these entries before switching sections, or discard only the
              unsaved edits.
            </p>
            <Button onClick={() => void save(true)} disabled={disabled}>
              Save and continue
            </Button>
            <Button
              onClick={() => {
                setDrafts({});
                setBaseline(null);
                go(pendingStage);
              }}
              disabled={busy}
            >
              Discard unsaved changes
            </Button>
            <Button onClick={() => setPendingStage(null)}>Keep editing</Button>
          </div>
        )}
        <footer className="rb-actionbar">
          <div>
            <Button
              disabled={busy || index === 0}
              onClick={() => move(blueprint.stages[index - 1].id)}
            >
              <ArrowLeft size={16} />
              Back
            </Button>
            <span className="rb-save-notice" role="status">
              {busy ? "Saving…" : notice || (dirty ? "Unsaved changes" : "")}
            </span>
          </div>
          <div>
            {writable && (
              <Button
                onClick={() => void save(false)}
                disabled={disabled || !dirty}
              >
                Save draft
              </Button>
            )}
            {index < blueprint.stages.length - 1 && (
              <Button
                variant="primary"
                disabled={
                  busy || (writable && (blocked || Boolean(draftProblem)))
                }
                onClick={() =>
                  writable
                    ? void save(true, stage.id === "return-profile")
                    : move(blueprint.stages[index + 1].id)
                }
              >
                {stage.id === "return-profile"
                  ? "Build this return"
                  : "Continue"}
                <ArrowRight size={17} />
              </Button>
            )}
          </div>
        </footer>
        {role === "workspace_admin" && onTechnicalDetails && (
          <details className="rb-extra-details">
            <summary>Administrator tools</summary>
            <Button
              onClick={() =>
                onTechnicalDetails({
                  title: "Return Builder technical details",
                  facts: effective.facts,
                  answers: effective.answers,
                })
              }
            >
              Technical Details
            </Button>
          </details>
        )}
      </section>
    </div>
  );
}
